import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { isApiError, identity } from '@repletoryreach/sdk-api';
import { Stack, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, PasswordInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { PasswordRules } from '../../../../app/components/PasswordRules';
import { isPasswordValid, passwordRefusalMessage } from '../../../../lib/password';
import { COUNTRIES, DEFAULT_COUNTRY } from '../../../../lib/countries';
import { ReferralCodeField, SIGNUP_REF_KEY } from '../../../refer/components/ReferralCodeField';
import { useT } from '../../../../i18n/I18nProvider';

export function SignupForm() {
  const t = useT();
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [orgName, setOrgName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  // Prefilled from a partner's link; `fromLink` distinguishes that from a code
  // typed by hand, which is the only difference the server records.
  const [referral, setReferral] = useState<string>(() => {
    try { return sessionStorage.getItem(SIGNUP_REF_KEY) ?? ''; } catch { return ''; }
  });
  const [fromLink] = useState<boolean>(() => {
    try { return Boolean(sessionStorage.getItem(SIGNUP_REF_KEY)); } catch { return false; }
  });
  const [error, setError] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailTaken(false);
    setPasswordError(null);

    /*
     * Checked here rather than after a round trip. The old hint also omitted
     * the lowercase rule the service enforces, so a user following it literally
     * with `PASSWORD1!` was rejected in framework English
     * (auth-signup.md §4 item 1, §3).
     */
    if (!isPasswordValid(password)) {
      setPasswordError(t('password.rules.summary'));
      return;
    }

    setBusy(true);
    try {
      // `orgName` reached `Slugify` with whatever whitespace was typed.
      const code = referral.trim().toUpperCase().replace(/[\s-]/g, '');
      await signup(email.trim(), password, orgName.trim(), country,
        code ? { code, source: fromLink ? 'link' : 'code_at_signup' } : undefined);
      try { sessionStorage.removeItem(SIGNUP_REF_KEY); } catch { /* storage may be unavailable */ }

      // The shell greeted people as `thandi@acme.co.za` until onboarding step 3,
      // because DisplayName defaults to the email server-side.
      const name = displayName.trim();
      if (name) {
        try { await identity.updateProfile({ display_name: name }); } catch { /* non-fatal */ }
      }

      // First run → guided onboarding wizard.
      navigate('/onboarding', { replace: true });
    } catch (err) {
      // The likeliest signup failure is a duplicate email, and the message
      // used to be framework prose with no route to sign-in or recovery.
      if (isApiError(err) && (err.status === 409 || /already|taken|duplicate/i.test(err.message))) {
        setEmailTaken(true);
      } else {
        setError(passwordRefusalMessage(err, t) ?? (err instanceof Error ? err.message : t('auth.signup.failed')));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label={t('auth.signup.form.orgName')}
          value={orgName}
          onChange={(e) => setOrgName(e.currentTarget.value)}
          required
          placeholder={t('auth.signup.form.orgNamePlaceholder')}
          autoFocus
        />
        <TextInput
          label={t('auth.signup.form.name')}
          value={displayName}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
          placeholder={t('auth.signup.form.namePlaceholder')}
          autoComplete="name"
        />
        <TextInput
          label={t('auth.signup.form.workEmail')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          autoComplete="email"
          placeholder={t('auth.login.emailPlaceholder')}
        />
        <Select
          label={t('auth.signup.form.country')}
          value={country}
          onChange={(e) => setCountry(e.currentTarget.value)}
          data={COUNTRIES.map((c) => ({ value: c.value, label: t(c.labelKey) }))}
        />
        <ReferralCodeField value={referral} onChange={setReferral} />
        <Stack gap={4}>
          <PasswordInput
            label={t('auth.common.password')}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            required
            autoComplete="new-password"
            description={t('password.rules.summary')}
            error={passwordError}
          />
          {password.length > 0 && <PasswordRules value={password} />}
        </Stack>

        {emailTaken && (
          <Alert color="warning" role="alert">
            {t('auth.signup.emailTaken')}{' '}
            <Link to="/auth/login">{t('auth.signup.emailTaken.signIn')}</Link>
            {' · '}
            <Link to="/auth/forgot-password">{t('auth.signup.emailTaken.reset')}</Link>
          </Alert>
        )}
        {error && <Alert color="error" role="alert">{error}</Alert>}

        <Button type="submit" loading={busy} fullWidth>{t('auth.signup.submit')}</Button>
      </Stack>
    </form>
  );
}
